import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { apiCountries } from '../api/client.js';
import Modal from '../components/Modal.jsx';
import EntityActions from '../components/EntityActions.jsx';
import ArchivedFilter from '../components/ArchivedFilter.jsx';

/**
 * Sprint 5 — Vue CRUD admin des Pays.
 *
 * PK = code ISO 3166-1 alpha-2 (2 lettres). Le code est éditable à la création
 * uniquement (immuable côté PATCH — Prisma refuserait de toute façon car
 * `Country` est référencé par Person.countryCode FK).
 */

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtn = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

const EMPTY = { code: '', name: '' };

export default function CountriesView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [archivedFilter, setArchivedFilter] = useState('active');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items: data } = await apiCountries.list(archivedFilter);
      setItems(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [archivedFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ code: c.code, name: c.name });
    setFormError(null);
    setModalOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      if (editing) {
        await apiCountries.update(editing.code, { name: form.name.trim() });
      } else {
        await apiCountries.create({
          code: form.code.trim().toUpperCase(),
          name: form.name.trim(),
        });
      }
      setModalOpen(false);
      await reload();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onArchive = async (c) => {
    if (!window.confirm(`Archiver le pays "${c.name}" (${c.code}) ?`)) return;
    await apiCountries.archive(c.code);
    await reload();
  };

  const onRestore = async (c) => {
    await apiCountries.restore(c.code);
    await reload();
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--bg)',
          paddingBottom: 12,
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)' }}>Pays</h1>
        <ArchivedFilter value={archivedFilter} onChange={setArchivedFilter} />
        <button type="button" onClick={openCreate} style={{ ...primaryBtn, marginLeft: 'auto' }}>
          <Plus size={14} /> Nouveau pays
        </button>
      </div>

      {error ? <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div> : null}

      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 80 }}>Code</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Nom</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Régions</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Personnes</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Fériés</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ padding: 20, color: 'var(--text-muted)' }}>Chargement…</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 20, color: 'var(--text-muted)' }}>Aucun pays.</td>
              </tr>
            ) : (
              items.map((c) => (
                <tr key={c.code} style={{ borderTop: '1px solid var(--glass-border)' }}>
                  <td style={{ padding: '10px 12px', color: 'var(--text)', fontWeight: 600 }}>
                    {c.code}
                    {c.archivedAt ? (
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>(archivé)</span>
                    ) : null}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text)' }}>{c.name}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{c._count?.regions ?? 0}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{c._count?.persons ?? 0}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{c._count?.publicHolidays ?? 0}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <EntityActions
                      archived={Boolean(c.archivedAt)}
                      onEdit={() => openEdit(c)}
                      onArchive={() => onArchive(c)}
                      onRestore={() => onRestore(c)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Modifier ${editing.code}` : 'Nouveau pays'}
        footer={
          <>
            <button type="button" onClick={() => setModalOpen(false)} style={secondaryBtn}>
              Annuler
            </button>
            <button
              type="submit"
              form="country-form"
              disabled={
                submitting ||
                !form.name.trim() ||
                (!editing && !/^[A-Za-z]{2}$/.test(form.code.trim()))
              }
              style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}
            >
              Enregistrer
            </button>
          </>
        }
      >
        <form id="country-form" onSubmit={onSubmit} style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Code ISO 3166-1 alpha-2
            </label>
            <input
              type="text"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().slice(0, 2) })}
              required
              maxLength={2}
              disabled={Boolean(editing)}
              placeholder="CH"
              autoFocus
              style={{ ...inputStyle, textTransform: 'uppercase', letterSpacing: '0.1em' }}
            />
            {editing ? (
              <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
                Le code n'est pas modifiable.
              </div>
            ) : null}
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Nom
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              maxLength={64}
              placeholder="Suisse"
              style={inputStyle}
            />
          </div>
          {formError ? <div style={{ color: 'var(--danger)', fontSize: 13 }}>{formError}</div> : null}
        </form>
      </Modal>
    </div>
  );
}
